/**
 * The sequence model (Studio): frames → a single laid-out timeline that the
 * timeline UI, the playhead, playback and export all agree on. Pure — every
 * consumer derives from the same numbers, so what you scrub is what exports.
 * Transitions occupy their own time between screens (matching record.ts,
 * which inserts transition frames rather than overlapping screen time).
 */
import { arrayToMap } from "@excalidraw/common";
import { getElementsOverlappingFrame } from "@excalidraw/element";

import type {
  ExcalidrawElement,
  ExcalidrawFrameLikeElement,
} from "@excalidraw/element/types";

import { getScreenCamera } from "./camera";
import { layoutElements } from "./layout";
import { getScreenTransition } from "./transitions";
import { getScreenDurationMs } from "./types";

import type { ScreenCamera } from "./camera";
import type { ScreenTransition } from "./transitions";

export type SequenceEntry = {
  frame: ExcalidrawFrameLikeElement;
  /** validated transition INTO this screen (null on the first screen) */
  transitionIn: ScreenTransition | null;
  /** where that transition window begins (null without a transition) */
  transitionStartMs: number | null;
  /** where this screen's own dwell time begins */
  startMs: number;
  durationMs: number;
  camera: ScreenCamera | null;
};

export type Sequence = {
  entries: SequenceEntry[];
  totalMs: number;
};

export const buildSequence = (
  frames: readonly ExcalidrawFrameLikeElement[],
  /** when given, a screen dwells at least as long as its compiled element
   * animations (cross-element sequencing routinely exceeds the configured
   * screenDurationMs) — mirrors record.ts's effectiveMs = max(configured,
   * compiled), so Studio scrub and export agree.
   * ponytail: O(frames × elements) per call and Studio calls this per scrub
   * tick; memoize by scene version if it ever shows in profiles */
  elements?: readonly ExcalidrawElement[],
): Sequence => {
  const entries: SequenceEntry[] = [];
  const elementsMap = elements ? arrayToMap(elements) : null;
  let cursor = 0;
  frames.forEach((frame, index) => {
    const transitionIn = index > 0 ? getScreenTransition(frame) : null;
    const transitionStartMs = transitionIn ? cursor : null;
    if (transitionIn) {
      cursor += transitionIn.durationMs;
    }
    const durationMs = Math.max(
      getScreenDurationMs(frame),
      elements
        ? layoutElements(
            getElementsOverlappingFrame(elements, frame, elementsMap!).filter(
              (el) => el.id !== frame.id,
            ),
          ).totalMs
        : 0,
    );
    entries.push({
      frame,
      transitionIn,
      transitionStartMs,
      startMs: cursor,
      durationMs,
      camera: getScreenCamera(frame),
    });
    cursor += durationMs;
  });
  return { entries, totalMs: cursor };
};

export type SequenceSample = {
  index: number;
  /** ms into the screen's own dwell (0 while inside its transition window) */
  localMs: number;
  /** set while inside the transition window INTO entries[index] */
  transition?: { fromIndex: number; progress: number };
};

/** Where a global sequence time lands: which screen, how far into it, and
 * whether we're mid-transition. Clamped at both ends. */
export const sampleSequence = (seq: Sequence, tMs: number): SequenceSample => {
  if (seq.entries.length === 0) {
    return { index: 0, localMs: 0 };
  }
  const t = Math.max(0, Math.min(seq.totalMs, tMs));
  for (let i = 0; i < seq.entries.length; i++) {
    const entry = seq.entries[i];
    if (
      entry.transitionIn &&
      entry.transitionStartMs !== null &&
      t < entry.startMs
    ) {
      if (t >= entry.transitionStartMs) {
        return {
          index: i,
          localMs: 0,
          transition: {
            fromIndex: i - 1,
            progress:
              (t - entry.transitionStartMs) / entry.transitionIn.durationMs,
          },
        };
      }
    }
    if (t < entry.startMs + entry.durationMs || i === seq.entries.length - 1) {
      if (t >= (entry.transitionStartMs ?? entry.startMs)) {
        return {
          index: i,
          localMs: Math.max(0, Math.min(entry.durationMs, t - entry.startMs)),
        };
      }
    }
  }
  const last = seq.entries.length - 1;
  return { index: last, localMs: seq.entries[last].durationMs };
};
